/**
 * The scene tools trace-viewer defines for every host (`SCENE_TOOLS`), as this
 * editor's tools: the viewer owns each tool's name, schema and behavior, the
 * gateway's editor kits own what the model reads about it (`kits.ts`), and this
 * file adds this editor's host.
 */

import { getUndoManager, sceneTool, toAnthropicTool, type CaptureRequest, type Refusal, type SceneToolHost, type ToolImage } from 'trace-viewer/editor';
import { getSceneRenderer } from '../../render-bridge';
import { placeCatalogAsset } from './place-catalog-asset';
import { collectRuntimeState } from '../../render-runtime-state';
import type { ToolContext } from '../agent-helpers';
import { authoringPorts, oneWiringAtATime, reportOrphaned, wiringRefusalText } from './authoring-ops';
import type { ToolDefinition } from './types';
import { getStatesSession } from '../../states-session';
import { isStatesEditingEnabled } from '../../states-editing-mode';

/** A refusal in this editor's words: where mate anchors come from (build_mesh), and the prompt's name for a handled event. */
function hostRefusalText(refusal: Refusal): string {
    if (refusal.code === 'INVALID_MATE') return refusal.message.replace(/It has none\.$/, 'It has none: anchors come from anchor() in its build_mesh code.');
    return wiringRefusalText(refusal);
}

/**
 * A headless screenshot: the scene goes out through the product's own export
 * path to a host that can render (render-bridge.ts) and comes back as an image,
 * from this call's framing or else the last pose focus_camera settled on.
 *
 * In play mode every behavior serialises its authored data, so the render page
 * would re-derive each bound transform from its declared value and snap the
 * scene back to rest; the live values ride along so the picture is one scene.
 */
async function headlessCapture(ctx: ToolContext, request: CaptureRequest): Promise<ToolImage | string> {
    const renderer = getSceneRenderer();
    if (!renderer) return 'screenshots are unavailable in this headless environment (no render loop). Use get_scene_tree / get_transform to verify the scene instead; the layout you built is already committed.';
    try {
        const { data } = ctx.traceScene._buildExportData();
        const runtimeState = ctx.traceScene.isPlayMode() ? collectRuntimeState(ctx.traceScene) : [];
        const rendered = await renderer({
            scene: {
                sceneName: 'screenshot', version: 1, content: data,
                ...(runtimeState.length ? { runtimeState } : {}),
                ...(ctx.sceneEnvironment ? { environment: ctx.sceneEnvironment } : {}),
            },
            traceScene: ctx.traceScene,
            pose: request.pose ?? ctx.capturePose ?? null,
            width: request.width,
            height: request.height,
            finalReview: request.finalReview,
        });
        // A host may answer with a reason instead of an image (a spent budget, say).
        const match = /^data:(image\/(?:png|jpeg));base64,(.+)$/s.exec(rendered);
        return match ? { mimeType: match[1] as ToolImage['mimeType'], data: match[2] } : rendered;
    } catch (err) {
        return `headless render failed: ${err instanceof Error ? err.message : String(err)}`;
    }
}

/** This editor as a scene tool host: the open CMS scene's folder, its page-editing mode, its undo stack, selection and open States strip. */
export function sandboxHost(ctx: ToolContext): SceneToolHost {
    return {
        scene: ctx.traceScene,
        ports: authoringPorts,
        record: command => getUndoManager().push(command),
        selected: () => ctx.selectedObject,
        statesSession: () => (isStatesEditingEnabled() ? getStatesSession() : null),
        select: object => ctx.traceScene.select(object),
        changed: () => ctx.onSceneChanged?.(),
        refusalText: hostRefusalText,
        orphaned: reportOrphaned,
        capture: request => headlessCapture(ctx, request),
        importAsset: async request => {
            const { assetId, url, name, parent, pose } = request;
            const placed = await placeCatalogAsset({ assetId, url, name, parent, ...pose }, ctx);
            return placed.ok && placed.sceneObject ? placed.sceneObject : placed.message.replace(/^Error:\s*/, '');
        },
        capturePose: pose => { ctx.capturePose = pose; },
    };
}

const WIRING_NOTE = 'Verify with set_play_mode, simulate_select and real pointer picks (simulate_select bypasses ray-picking); save_scene to persist.';

/**
 * What this editor adds to a viewer scene tool: `note` is appended to a call
 * that did not fail (how to check it with this editor's own tools); `queued`
 * tools write behaviors, so they run in this editor's wiring queue with its own
 * wire tools.
 */
const HOSTING: Record<string, { note?: string; queued?: true }> = {
    focus_camera: { note: '(screenshot takes these same inputs — you do not need this call before one.)' },
    add_behavior: { queued: true },
    remove_behavior: { queued: true },
    commit_behavior: { queued: true },
    wire_clock: { note: 'Verify with set_play_mode(true), probe_runtime, pause/scrub/resume and save_scene.', queued: true },
    wire_interaction: { note: WIRING_NOTE, queued: true },
};

/** The viewer's scene tool `name`, run against this editor. */
export function sharedSceneTool(name: string): ToolDefinition {
    const shared = sceneTool(name);
    if (!shared) throw new Error(`[shared-scene-tools] trace-viewer has no scene tool "${name}"`);
    const { note, queued } = HOSTING[name] ?? {};
    return {
        tool: toAnthropicTool({ name, description: '', inputSchema: shared.inputSchema }) as ToolDefinition['tool'],
        execute: async (input, ctx) => {
            // This editor's own wiring tools share the queue, so no two edit one graph at once.
            const run = () => shared.run(input, sandboxHost(ctx));
            const outcome = await (queued ? oneWiringAtATime(ctx, run) : run());
            // A picture goes back as a data URL, which this editor's agent loop and MCP server show the model as an image.
            const [image] = outcome.images ?? [];
            if (image) return `data:${image.mimeType};base64,${image.data}`;
            return note && !outcome.isError ? `${outcome.text}\n${note}` : outcome.text;
        },
    };
}
